// ============================================
// FORMATAGE ET COULEURS
// ============================================
// Helpers purs, sans dépendance React. Extraits de AdminV2.tsx.

import { TicketStatus, TicketPriority } from '../types';

// Dates et heures : à l'heure de Paris, quel que soit le fuseau du navigateur
// (voir shared/dates.ts). Réexportées ici pour les écrans existants.
export { formatDateTime, formatTime } from './dates';

export const formatFileSize = (bytes?: number): string => {
  if (!bytes || isNaN(bytes)) return '-';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

/** Initiales d'un utilisateur, à partir de son nom ou, à défaut, de son e-mail. */
export const getInitials = (name?: string | null, email?: string | null): string => {
  if (name && name.trim()) {
    const parts = name.trim().split(' ').filter(n => n.length > 0);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  if (email) {
    const localPart = email.split('@')[0];
    return localPart.slice(0, 2).toUpperCase();
  }
  return 'U';
};

// Une couleur par statut, la même partout : badge, liseré, graphiques
// (demande de Nathan du 16/10/2026). Chaque badge porte aussi une pastille
// (classe .pastille-statut) : la couleur n'est jamais le seul repère.
//   Ouvert : bleu ciel (personne dessus) · En cours : indigo (un conseiller
//   travaille) · Attente client : ambre · Réouvert : orange (à reprendre) ·
//   Escaladé : rouge · Résolu : vert · Fermé : gris.
// Les mêmes teintes que tailwind.config.js (colors.statut.*).
export const COULEUR_STATUT: Record<TicketStatus, string> = {
  [TicketStatus.OPEN]: '#0284C7',
  [TicketStatus.IN_PROGRESS]: '#4F46E5',
  [TicketStatus.WAITING_CUSTOMER]: '#B26A00',
  [TicketStatus.REOPENED]: '#C2410C',
  [TicketStatus.ESCALATED]: '#B3261E',
  [TicketStatus.RESOLVED]: '#15803D',
  [TicketStatus.CLOSED]: '#64748B',
};

/** Fond, texte et contour clairs d'un badge de statut (sans pastille). */
export const CLASSES_STATUT: Record<TicketStatus, string> = {
  [TicketStatus.OPEN]: 'bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]',
  [TicketStatus.IN_PROGRESS]: 'bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]',
  [TicketStatus.WAITING_CUSTOMER]: 'bg-[#FBF1E1] text-[#7A4A00] border-[#F0D9AE]',
  [TicketStatus.REOPENED]: 'bg-[#FFEDD5] text-[#9A3412] border-[#FED7AA]',
  [TicketStatus.ESCALATED]: 'bg-[#FBE9E7] text-[#8A1C16] border-[#F2C4BF]',
  [TicketStatus.RESOLVED]: 'bg-[#DCFCE7] text-[#166534] border-[#BBF7D0]',
  [TicketStatus.CLOSED]: 'bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]',
};

/** Badge avec pastille. Les écrans utilisent <BadgeStatut> (shared/ui/BadgeStatut), qui porte une icône à la place. */
export const getStatusColor = (status: TicketStatus): string =>
  'pastille-statut ' + (CLASSES_STATUT[status] || 'bg-brume text-graphite border-trait');

/** Liseré de statut (classe .rail-*) pour une ligne ou une carte. */
export const getStatusRail = (status: TicketStatus): string => ({
  [TicketStatus.OPEN]: 'rail rail-nouveau',
  [TicketStatus.IN_PROGRESS]: 'rail rail-encours',
  [TicketStatus.WAITING_CUSTOMER]: 'rail rail-attente',
  [TicketStatus.REOPENED]: 'rail rail-reouvert',
  [TicketStatus.ESCALATED]: 'rail rail-urgent',
  [TicketStatus.RESOLVED]: 'rail rail-resolu',
  [TicketStatus.CLOSED]: 'rail rail-ferme',
}[status] || 'rail rail-ferme');

// Priorité : Basse gris, Moyenne bleu, Haute ambre, Urgente rouge (pastille
// du sélecteur de la fiche ; les badges utilisent getPriorityColor).
export const COULEUR_PRIORITE: Record<TicketPriority, string> = {
  [TicketPriority.LOW]: '#64748B',
  [TicketPriority.MEDIUM]: '#2563EB',
  [TicketPriority.HIGH]: '#B26A00',
  [TicketPriority.URGENT]: '#B3261E',
};

export const getPriorityColor = (priority: TicketPriority): string => ({
  [TicketPriority.LOW]: 'bg-brume text-graphite',
  [TicketPriority.MEDIUM]: 'bg-[#E7EEF6] text-[#274B73]',
  [TicketPriority.HIGH]: 'bg-[#FBF1E1] text-[#7A4A00]',
  [TicketPriority.URGENT]: 'bg-[#FBE9E7] text-[#8A1C16]',
}[priority] || 'bg-brume text-graphite');
